import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./construction.css";
import { useHeroBuilding, useProcessBuilding } from "./useConstructionScenes";
import { ConstructionWordMark } from "../Basic/ConstructionWordMark";
import yuvrajPhoto from "../../static/images/yuvrajConstruction.png";
import { sendInboxEmail } from "../../lib/sendInboxEmail";

const CONTACT_EMAIL = "info@readalsystems.com";
const MAILTO = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent("Readal Construction enquiry")}`;
const PAGE_TITLE =
  "Readal Construction — Residential, Commercial & Turnkey Construction";
const PAGE_DESCRIPTION =
  "Readal Construction delivers residential, commercial, civil and turnkey construction with clear estimates, defined project stages and transparent communication.";

function readSavedTheme() {
  try {
    return localStorage.getItem("tc-theme") || "";
  } catch {
    return "";
  }
}

export default function ReadalConstruction() {
  const rootRef = useRef(null);
  const [theme, setTheme] = useState(readSavedTheme);
  const heroCanvasRef = useHeroBuilding(theme);
  const processScene = useProcessBuilding(theme);
  const [enquiry, setEnquiry] = useState({
    name: "",
    phone: "",
    email: "",
    type: "Residential",
    message: "",
  });
  const [enquirySent, setEnquirySent] = useState(false);
  const [enquirySending, setEnquirySending] = useState(false);
  const [enquiryError, setEnquiryError] = useState("");

  useEffect(() => {
    const html = document.documentElement;
    const previousTitle = document.title;
    const meta =
      document.querySelector('meta[name="description"]') ||
      (() => {
        const created = document.createElement("meta");
        created.setAttribute("name", "description");
        document.head.appendChild(created);
        return created;
      })();
    const previousDescription = meta.getAttribute("content");

    html.classList.add("tc-page");
    document.title = PAGE_TITLE;
    meta.setAttribute("content", PAGE_DESCRIPTION);

    return () => {
      html.classList.remove("tc-page", "tc-dark", "tc-light");
      document.title = previousTitle;
      if (previousDescription != null) {
        meta.setAttribute("content", previousDescription);
      }
    };
  }, []);

  useEffect(() => {
    const html = document.documentElement;
    html.classList.toggle("tc-dark", theme === "dark");
    html.classList.toggle("tc-light", theme === "light");
  }, [theme]);

  function toggleTheme() {
    const root = rootRef.current;
    if (!root) return;
    const dark =
      getComputedStyle(root).getPropertyValue("color-scheme").trim() === "dark";
    const next = dark ? "light" : "dark";
    setTheme(next);
    try {
      localStorage.setItem("tc-theme", next);
    } catch {
      /* ignore quota / private mode */
    }
  }

  function handleEnquiryChange(event) {
    const { name, value } = event.target;
    setEnquiry((current) => ({ ...current, [name]: value }));
  }

  async function handleEnquirySubmit(event) {
    event.preventDefault();
    setEnquirySending(true);
    setEnquiryError("");
    try {
      await sendInboxEmail(
        {
          name: enquiry.name,
          phone: enquiry.phone,
          email: enquiry.email,
          project_type: enquiry.type,
          message: enquiry.message,
        },
        "Readal Construction enquiry"
      );
      setEnquirySent(true);
    } catch {
      setEnquiryError(
        `Could not send just now. Email us at ${CONTACT_EMAIL}.`
      );
    } finally {
      setEnquirySending(false);
    }
  }

  return (
    <div
      className="tc-root"
      ref={rootRef}
      data-theme={theme || undefined}
    >
      <header className="masthead">
        <div className="wrap">
          <a className="mark" href="#top">
            <svg width="30" height="30" viewBox="0 0 30 30" aria-hidden="true">
              <rect
                x="1"
                y="1"
                width="28"
                height="28"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
              />
              <path
                d="M6 22 L15 8 L24 22"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
              />
              <rect x="11.5" y="16" width="7" height="6" fill="var(--hiviz)" />
            </svg>
            <span>
              <b>Readal Construction</b>
              <span>Hamirpur, Himachal Pradesh</span>
            </span>
          </a>
          <nav className="nav" aria-label="Sections">
            <a href="#build">What we build</a>
            <a href="#process">Process</a>
            <a href="#capabilities">Capabilities</a>
            <a href="#about">About</a>
            <a href="#projects">Projects</a>
            <a href="#contact">Contact</a>
          </nav>
          <a className="btn small header-cta" href="#contact">
            <span className="cta-full">Request a quote</span>
            <span className="cta-short">Quote</span>
          </a>
        </div>
      </header>

      <main id="top">
        <section className="hero">
          <div className="wrap">
            <div>
              <h1>
                Building with strength.
                <em>Delivering with responsibility.</em>
              </h1>
              <p>
                From groundwork to final finishing, we plan, build and hand over
                residential and commercial projects that hold up for decades.
              </p>
              <div className="hero-actions">
                <a className="btn" href="#contact">
                  Request a quote
                </a>
                <a className="btn ghost" href="#contact">
                  Discuss your project
                </a>
              </div>
              <div className="scope">
                <i>Residential</i>
                <i>Commercial</i>
                <i>Civil works</i>
                <i>Renovation</i>
                <i>Turnkey construction</i>
              </div>
            </div>

            <figure className="hero-visual">
              <canvas
                ref={heroCanvasRef}
                className="hero-canvas"
                aria-label="Three-dimensional wireframe of a two-storey house being assembled"
              />
              <svg
                className="drawing drawing-fallback"
                viewBox="0 0 520 430"
                role="img"
                aria-hidden="true"
              >
                <g className="grid-line">
                  <path d="M0 40H520M0 100H520M0 160H520M0 220H520M0 280H520M0 340H520" />
                  <path d="M60 0V430M140 0V430M220 0V430M300 0V430M380 0V430M460 0V430" />
                </g>

                <path className="dim" pathLength="1" d="M28 372H500" />
                <path
                  className="dim"
                  pathLength="1"
                  d="M40 380l10-8M70 380l10-8M100 380l10-8M130 380l10-8M160 380l10-8M190 380l10-8M220 380l10-8M250 380l10-8M280 380l10-8M310 380l10-8M340 380l10-8M370 380l10-8M400 380l10-8M430 380l10-8M460 380l10-8M490 380l10-8"
                />

                <path className="fillp" pathLength="1" d="M110 372V150h250v222z" />
                <path className="ink d2" pathLength="1" d="M110 150 235 78l125 72" />
                <path className="ink d2" pathLength="1" d="M110 262h250" />

                <g className="ink d3">
                  <path
                    pathLength="1"
                    d="M142 186h58v52h-58zM232 186h58v52h-58zM302 186h34v52h-34z"
                  />
                  <path
                    pathLength="1"
                    d="M171 186v52M142 212h58M261 186v52M232 212h58"
                  />
                  <path
                    pathLength="1"
                    d="M142 296h52v50h-52zM280 296h60v50h-60z"
                  />
                  <path pathLength="1" d="M310 296v50" />
                  <path pathLength="1" d="M216 290h48v82h-48z" />
                </g>

                <path className="ink d3" pathLength="1" d="M110 356h250" />

                <g className="dim d4">
                  <path pathLength="1" d="M78 150v222M72 150h12M72 372h12" />
                  <path pathLength="1" d="M110 400h250M110 394v12M360 394v12" />
                  <path pathLength="1" d="M396 78v72M390 78h12M390 150h12" />
                </g>

                <rect className="hi" x="216" y="290" width="48" height="6" />
                <text x="62" y="266" transform="rotate(-90 62 266)">
                  Two floors
                </text>
                <text x="216" y="418">
                  Built-up width
                </text>
                <text x="406" y="118">
                  Roof
                </text>
              </svg>
            </figure>
          </div>
        </section>

        <section className="intro">
          <div className="wrap">
            <h2>Construction you can build your future on</h2>
            <div className="prose">
              <p>
                Readal Construction delivers practical, durable and well-managed
                construction for residential and commercial projects.
              </p>
              <p>
                Construction is more than putting together concrete, steel and
                bricks. It is understanding what a client wants to build, planning
                it properly, using the right resources, holding quality through
                every stage, and handing over something that will stand for years.
              </p>
              <p>
                Whether you are planning a new home, developing a commercial
                property, renovating an existing building or need support with
                civil works, our job is to keep the process organised, transparent
                and dependable.
              </p>
            </div>
          </div>
        </section>

        <section id="build">
          <div className="wrap">
            <div className="section-head">
              <h2>What we build</h2>
              <p>
                Five areas of work, each managed with the same planning,
                supervision and quality checks — whether it is one floor or a full
                development.
              </p>
            </div>

            <article className="service">
              <div>
                <h3>Residential construction</h3>
                <p>
                  Independent houses, family homes and villas, built around
                  structural quality, finishing and how people will actually live
                  in the space.
                </p>
              </div>
              <ul className="tags">
                <li>New house construction</li>
                <li>Villa construction</li>
                <li>Structural and civil work</li>
                <li>Home extensions</li>
                <li>Floor additions</li>
                <li>Renovation and remodelling</li>
                <li>Finishing work</li>
                <li>Repair and restoration</li>
              </ul>
            </article>

            <article className="service">
              <div>
                <h3>Commercial construction</h3>
                <p>
                  Spaces planned around usability, durability, utilities and the
                  way the business will run once it opens.
                </p>
              </div>
              <ul className="tags">
                <li>Offices</li>
                <li>Retail spaces</li>
                <li>Shops and showrooms</li>
                <li>Hospitality spaces</li>
                <li>Small commercial developments</li>
                <li>Warehouses and utility structures</li>
                <li>Commercial renovation</li>
                <li>Fit-outs</li>
              </ul>
            </article>

            <article className="service">
              <div>
                <h3>Renovation and improvement</h3>
                <p>
                  Not every property needs to be rebuilt. Repairs, expansion and
                  modernisation run as one coordinated project instead of a dozen
                  separate contractors.
                </p>
              </div>
              <ul className="tags">
                <li>Structural changes</li>
                <li>Flooring</li>
                <li>Walls and ceilings</li>
                <li>Electrical</li>
                <li>Plumbing</li>
                <li>Painting and finishing</li>
              </ul>
            </article>

            <article className="service">
              <div>
                <h3>Civil and structural works</h3>
                <p>
                  Strong construction begins with the work people eventually stop
                  seeing. These stages get full attention before anything moves
                  toward finishes.
                </p>
              </div>
              <ul className="tags">
                <li>Foundations</li>
                <li>Structural elements</li>
                <li>Masonry</li>
                <li>Waterproofing</li>
                <li>Drainage</li>
                <li>Concrete work</li>
              </ul>
            </article>

            <article className="service">
              <div>
                <h3>Turnkey construction</h3>
                <p>
                  One project, one team, one point of responsibility — from the
                  first site visit through to final handover. You deal with us; we
                  coordinate the rest.
                </p>
              </div>
              <ul className="tags">
                <li>Site assessment</li>
                <li>Planning and estimation</li>
                <li>Material procurement</li>
                <li>Labour coordination</li>
                <li>Civil construction</li>
                <li>Electrical coordination</li>
                <li>Plumbing coordination</li>
                <li>Flooring and tiling</li>
                <li>Doors and windows</li>
                <li>Painting and finishing</li>
                <li>Quality inspections</li>
                <li>Final handover</li>
              </ul>
            </article>
          </div>
        </section>

        <section className="process" id="process" ref={processScene.sectionRef}>
          <div className="wrap">
            <div className="section-head">
              <h2>From empty ground to finished space</h2>
              <p>
                Six stages, in order. Each one has something you can see, inspect
                and sign off before the next begins.
              </p>
            </div>
            <div className="process-visual">
              <canvas
                ref={processScene.canvasRef}
                className="process-canvas"
                aria-label="A site that is surveyed, then built through the six construction stages"
              />
              <p className="process-caption" ref={processScene.captionRef}>
                Stage 01 — Consultation
              </p>
            </div>
            <ol className="steps">
              <li>
                <b>Stage 01</b>
                <h3>Consultation</h3>
                <p>
                  We start with the property, your requirements, expectations,
                  approximate budget and intended timeline.
                </p>
              </li>
              <li>
                <b>Stage 02</b>
                <h3>Site assessment</h3>
                <p>
                  The site is reviewed to understand existing conditions and the
                  practical requirements of the proposed work.
                </p>
              </li>
              <li>
                <b>Stage 03</b>
                <h3>Planning and estimation</h3>
                <p>
                  Scope of work, resources, stages, materials and estimated costs
                  are defined and put in writing.
                </p>
              </li>
              <li>
                <b>Stage 04</b>
                <h3>Project execution</h3>
                <p>
                  Construction runs to the agreed scope, coordinating labour,
                  materials, vendors and the different trades.
                </p>
              </li>
              <li>
                <b>Stage 05</b>
                <h3>Quality checks</h3>
                <p>
                  Work is reviewed throughout construction rather than leaving
                  every check until the end of the project.
                </p>
              </li>
              <li>
                <b>Stage 06</b>
                <h3>Completion and handover</h3>
                <p>
                  Final work is inspected, finishing items are closed out, and the
                  project is prepared for handover.
                </p>
              </li>
            </ol>
          </div>
        </section>

        <section id="capabilities">
          <div className="wrap">
            <div className="section-head">
              <h2>Built on more than promises</h2>
              <p>
                A construction company should be able to show it has the resources
                to execute the work it accepts. Here is ours, open to inspection.
              </p>
            </div>
            <div className="caps">
              <div>
                <h3>Licences and registrations</h3>
                <p>
                  Business registrations, licences, certifications and supporting
                  documentation can be provided according to project requirements.
                </p>
                <a className="linkout" href={MAILTO}>
                  View credentials
                </a>
              </div>
              <div>
                <h3>Tools and equipment</h3>
                <p>
                  We maintain access to the tools, machinery and supporting
                  resources required for the construction activities within our
                  scope.
                </p>
                <a className="linkout" href={MAILTO}>
                  View equipment
                </a>
              </div>
              <div>
                <h3>Workforce and specialists</h3>
                <p>
                  Projects need different skills at different stages. We
                  coordinate the trades required for civil, electrical, plumbing
                  and finishing work.
                </p>
                <a className="linkout" href={MAILTO}>
                  Ask about our team
                </a>
              </div>
            </div>
          </div>
        </section>

        <section>
          <div className="wrap">
            <div className="section-head">
              <h2>Why Readal Construction</h2>
              <p>
                Six commitments that shape how every project on our books is
                priced, built and handed over.
              </p>
            </div>
            <div className="reasons">
              <div>
                <h3>Clear estimates</h3>
                <p>
                  Scope and expected costs are defined up front, so you know what
                  you are paying for before work starts.
                </p>
              </div>
              <div>
                <h3>Quality-focused execution</h3>
                <p>
                  Construction decisions are made with durability, safety,
                  workmanship and long-term usability in mind.
                </p>
              </div>
              <div>
                <h3>Transparent communication</h3>
                <p>
                  You should know what is happening on your project. We keep you
                  updated through every stage of construction.
                </p>
              </div>
              <div>
                <h3>Defined project stages</h3>
                <p>
                  Breaking construction into milestones makes progress easier to
                  understand, inspect and manage.
                </p>
              </div>
              <div>
                <h3>Responsible material selection</h3>
                <p>
                  Materials affect both immediate cost and the life of the
                  building. We balance quality, requirements and budget.
                </p>
              </div>
              <div>
                <h3>Accountability</h3>
                <p>
                  We take responsibility for the work in our agreed scope, instead
                  of leaving you to coordinate every activity yourself.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="leader" id="about">
          <div className="wrap">
            <figure className="leader-photo">
              <img
                src={yuvrajPhoto}
                alt="Yuvraj Thakur, Founder and CEO of Thakur Constructions"
              />
            </figure>
            <div className="leader-copy">
              <h2>Yuvraj Thakur</h2>
              <h3>Founder &amp; CEO, Thakur Constructions</h3>
              <p>
                Yuvraj Thakur is the Founder and CEO of Thakur Constructions,
                leading the company with a clear focus on quality construction,
                responsible execution, and long-term client relationships.
              </p>
              <p>
                His vision for Thakur Constructions is simple: build with
                integrity, maintain high standards at every stage, and deliver
                spaces that are made to last.
              </p>
              <p>
                With a hands-on approach to operations, Yuvraj oversees project
                planning, resource coordination, quality standards, and
                execution. He believes that successful construction is not only
                about completing a structure—it is about understanding the
                client&apos;s vision, respecting their investment, and taking
                responsibility for every commitment made.
              </p>
              <p>
                Under his leadership, Thakur Constructions aims to establish
                itself as a trusted name across residential, commercial,
                renovation, civil, and turnkey construction projects.
              </p>
              <blockquote>
                <p>
                  “We don&apos;t just construct buildings. We build trust through
                  every project we deliver.”
                </p>
                <cite>Yuvraj Thakur</cite>
              </blockquote>
            </div>
          </div>
        </section>

        <section className="projects" id="projects">
          <div className="wrap">
            <div>
              <h2>Every project starts with a plan</h2>
              <p>
                Residential, commercial, renovation and civil work — each with a
                different site, budget and set of constraints. The objective stays
                the same: build it properly.
              </p>
            </div>
            <div className="stack">
              <a className="btn" href={MAILTO}>
                View completed projects
              </a>
              <a className="btn ghost" href={MAILTO}>
                Follow current sites
              </a>
            </div>
          </div>
        </section>

        <section className="enquiry" id="contact">
          <div className="wrap">
            <div className="prose">
              <h2>Have land? Have a plan? Start here.</h2>
              <p>
                Whether you already have architectural drawings or are still
                working out where to begin, tell us what you are planning.
              </p>
              <p>
                We will talk through your requirements, look at the site and help
                work out the next practical step.
              </p>
            </div>

            <div className="contact-panel">
              {enquirySent ? (
                <p className="confirm" role="status">
                  Request sent to {CONTACT_EMAIL}. We will reply within two
                  working days.
                </p>
              ) : (
                <form className="enquiry-form" onSubmit={handleEnquirySubmit}>
                  <div className="field">
                    <label htmlFor="tc-name">Name</label>
                    <input
                      id="tc-name"
                      name="name"
                      autoComplete="name"
                      value={enquiry.name}
                      onChange={handleEnquiryChange}
                      required
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="tc-phone">Phone number</label>
                    <input
                      id="tc-phone"
                      name="phone"
                      type="tel"
                      autoComplete="tel"
                      value={enquiry.phone}
                      onChange={handleEnquiryChange}
                      required
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="tc-email">Email address</label>
                    <input
                      id="tc-email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      value={enquiry.email}
                      onChange={handleEnquiryChange}
                      required
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="tc-type">Project type</label>
                    <select
                      id="tc-type"
                      name="type"
                      value={enquiry.type}
                      onChange={handleEnquiryChange}
                    >
                      <option>Residential</option>
                      <option>Commercial</option>
                      <option>Renovation</option>
                      <option>Civil work</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div className="field full">
                    <label htmlFor="tc-message">Tell us about your project</label>
                    <textarea
                      id="tc-message"
                      name="message"
                      value={enquiry.message}
                      onChange={handleEnquiryChange}
                      required
                    />
                  </div>
                  {enquiryError ? (
                    <p className="form-note" role="alert">
                      {enquiryError}{" "}
                      <a href={MAILTO}>{CONTACT_EMAIL}</a>
                    </p>
                  ) : null}
                  <div className="field full">
                    <button
                      className="btn bright"
                      type="submit"
                      disabled={enquirySending}
                    >
                      {enquirySending ? "Sending…" : "Request consultation"}
                    </button>
                  </div>
                  <p className="form-note">
                    Submissions are emailed to {CONTACT_EMAIL}. We reply within
                    two working days.
                  </p>
                </form>
              )}
            </div>
          </div>
        </section>

        <section className="closing">
          <div className="wrap">
            <h2>Let's build something that lasts</h2>
            <p>
              A building represents years of planning, investment and ambition.
              Our job is to turn that investment into something strong, functional
              and worth being proud of.
            </p>
            <div className="hero-actions">
              <a className="btn" href="#contact">
                Start your project
              </a>
              <a className="btn ghost" href="#contact">
                Contact us
              </a>
            </div>
          </div>
        </section>
      </main>

      <ConstructionWordMark />

      <footer>
        <div className="wrap">
          <div>
            <b>Readal Construction</b>
            <div className="svc">
              Construction · Renovation · Civil works · Turnkey projects
            </div>
            <div className="svc">
              <a href={MAILTO}>{CONTACT_EMAIL}</a>
            </div>
            <div className="svc">
              An initiative by <Link to="/">Readal Systems</Link>
            </div>
          </div>
          <button className="theme-toggle" type="button" onClick={toggleTheme}>
            Switch theme
          </button>
        </div>
      </footer>
    </div>
  );
}
